import { Flex, Heading, Skeleton } from "@chakra-ui/react"

import { Graph, Timeseries, Toggles } from "@hydrologiq/schemas/1.0.0/installationPage"
import { TimePoint, convertTimeSeriesData } from "@hydrologiq/common/types/graph/timeseries"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { resolution } from "./resolution"
import { nowDateTime } from "@hydrologiq/common/helpers/date"

import { energy } from "@hydrologiq/common/transformations/data/energy"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { useEffect, useState } from "react"
import ConfigureToggles from "./ConfigureToggles"
import TSWrapper from "./TSWrapper"

import { DateTime, seconds } from "timezonecomplete"

interface TimeSeriesGraphProps {
  graph: Graph
  timeseries: Timeseries
  endDate: DateTime
  data: any
  comparison?: { label: string; data: gqlTypes.TimeSeriesData[] }
}

export function TimeSeriesGraphSkeleton() {
  return (
    <Flex height="530px" position="absolute" width="100%" zIndex={1000}>
      <Skeleton height="100%" width="100%" opacity={1} animation={"none"} />
    </Flex>
  )
}

function TimeSeriesGraphImpl({ graph, timeseries, endDate, data, comparison }: TimeSeriesGraphProps) {
  const [toggles, setToggles] = useState<Toggles | undefined>(graph.toggles && structuredClone(graph.toggles))
  const [showPeak, setShowPeak] = useState<boolean>(false)
  const now = nowDateTime()

  const endResolutionS = resolution(timeseries.endResolution.quantity, timeseries.endResolution.resolution)
  let points: TimePoint[] = []
  let peaks: TimePoint[] = []
  let minWeights: TimePoint[] = []
  if (data.values) points = convertTimeSeriesData(data.values as gqlTypes.TimeSeriesData[])

  const peaksLabel = comparison ? comparison.label : "Peaks"

  if (showPeak) {
    if (data.peaks) peaks = convertTimeSeriesData(data.peaks as gqlTypes.TimeSeriesData[])
    else if (comparison) peaks = convertTimeSeriesData(comparison.data)
  }

  if (data.mw) {
    minWeights = convertTimeSeriesData(data.mw as gqlTypes.TimeSeriesData[])
    let weights: TimePoint[] = []
    minWeights.forEach((value, index) => {
      const next = index < minWeights.length ? minWeights[index + 1] : undefined
      weights.push(value)
      weights.push({
        ...value,
        time: next ? next.time.sub(seconds(1)) : now.add(seconds(endResolutionS)),
      })
    })
    minWeights = weights

    const refuellingGroups: [DateTime, TimePoint | undefined][] = []
    minWeights.forEach((minWeight) => {
      const time = minWeight.time
      if (
        refuellingGroups.filter(
          (value) => value[1] && ((time.unixUtcMillis() - value[1].time.unixUtcMillis()) * -1) / 1000 / 60 > 60
        ).length == 0
      ) {
        const matchingWeight = minWeights.filter((value) => {
          const timeDiff = ((time.unixUtcMillis() - value.time.unixUtcMillis()) * -1) / 1000 / 60
          return time !== value.time && timeDiff > 60 && timeDiff < 60 * 24
        })
        if (matchingWeight.length > 0)
          refuellingGroups.push([time, matchingWeight.length > 0 ? matchingWeight[0] : undefined])
      }
    })
    // Remove non-zero minimum weight
    const zeroValueRefuellingGroups = refuellingGroups
      .filter((value) => value[1] && value[1].value == 0)
      .map((value) => [value[0], value[1]?.time])
    points = points.map((point, index) => {
      const groups = zeroValueRefuellingGroups.filter(
        (group) =>
          group[0] &&
          group[1] &&
          point.time.unixUtcMillis() >= group[0].unixUtcMillis() &&
          point.time.unixUtcMillis() <= group[1].unixUtcMillis() + 60 * 1000 * 60
      )
      if (groups.length != 0) {
        if (index != 0) point.value = index == 0 ? 0 : points[index - 1].value
      }
      return point
    })
  }

  if (graph.source.sourceUnit && graph.source.desiredUnit) {
    points.forEach((value, index, arr) => {
      const newVal = energy(graph.source.sourceUnit as EnergyUnit, graph.source.desiredUnit as EnergyUnit, value.value)
      arr[index].value = newVal
    })
    if (showPeak && peaks.length > 0)
      peaks.forEach((value, index, arr) => {
        const newVal = energy(
          graph.source.sourceUnit as EnergyUnit,
          graph.source.desiredUnit as EnergyUnit,
          value.value
        )
        arr[index].value = newVal
      })
  }

  const peakToggleCallback = (toggles: Toggles) => {
    setToggles(toggles)
  }

  useEffect(() => {
    if (toggles) {
      setShowPeak((toggles && toggles.peak && toggles.peak.status) || false)
    }
  }, [toggles])

  const [loaded, setLoaded] = useState<boolean>(false)

  const togglesEnabled = toggles && toggles.peak

  let groups = [
    {
      name: "Points",
      points: points,
    },
    {
      name: peaksLabel,
      points: peaks,
    },
    {
      name: "Minimum weight",
      points: minWeights,
    },
  ]

  return (
    <>
      {!loaded && <TimeSeriesGraphSkeleton />}
      <Flex mb={togglesEnabled ? 0 : 8} alignItems={"center"}>
        <Heading fontFamily="montserrat" size="sm">
          {graph.title}{" "}
        </Heading>

      </Flex>

      {toggles && (
        <ConfigureToggles
          key={`${toggles.peak}`}
          toggles={toggles}
          changeCallback={peakToggleCallback}
          comparisonLabel={comparison?.label}
        />
      )}
      <TSWrapper
        groups={groups}
        valueAxis={{ title: graph.yAxis }}
        startResolutionS={resolution(timeseries.startResolution.quantity, timeseries.startResolution.resolution)}
        endResolutionS={60}
        lineDate={endDate}
        timeAxisScale={timeseries.scale}
        interval={timeseries.interval?.quantity}
        intervalResolution={timeseries.interval?.resolution}
        minYValue={graph.yAxisMinValue}
        unit={(graph.source.desiredUnit as EnergyUnit) || (graph.source.sourceUnit as EnergyUnit)}
        onLoaded={() => {
          setLoaded(true)
        }}
        groupLabels={comparison && ["Average", comparison.label, "Minimum weight"]}
      />
    </>
  )
}

export default TimeSeriesGraphImpl
